<template>
  <!-- todo: this will need to be abstracted more, but putting it off for this round -->
  <div
    role="dialog"
    aria-labelledby="directoryDetailsTitle"
    aria-describedby="directoryDetailsContent"
    class="tgbDirectoryDetails"
  >
    <!-- todo: this sort of classing with the component isolation is not ideal,
        we need module css imports or more flexible card component but I don't have
        time to think through the complexity this week -->
    <div>
      <div class="TgbDirectoryDetailsContent">
        <div class="tgbDirectoryBeforeTitle">
          <slot name="beforeTitle" />
        </div>

        <ul class="tgbDirectoryDetailsList">
          <!-- Production Name -->
          <li class="grid-col-span-2">
            <h2 class="tgbDirectoryDetailsTitle tgbTitleLg">{{ details.title }}</h2>
          </li>

          <!-- Production Owner -->
          <!-- Hide this section if user is only on one team -->
          <li v-if="hasTeams" class="grid-col-span-2">
            <h3 class="tgbDirectoryDetailsLabel">
              Team
            </h3>

            <div
              :title="ownedBy"
              class="user-details-row"
            >
              <img
                v-if="details.ownedBy && details.ownedBy.profileImageUrl"
                class="tgbUserIconInline"
                :src="details.ownedBy.profileImageUrl"
                :alt="`Profile image of ${details.ownedBy.displayName}`"
                @error="$event.target.src=defaultAvatar"
              />

              <span>{{ ownedBy }}</span>
            </div>
          </li>

          <!-- Created By -->
          <li>
            <h3 class="tgbDirectoryDetailsLabel">Created By</h3>

            <div
              :title="createdBy"
              class="user-details-row"
            >
              <img
                v-if="details.createdBy && details.createdBy.profileImageUrl"
                class="tgbUserIconInline"
                :src="details.createdBy.profileImageUrl"
                :alt="`Profile image of ${details.createdBy.displayName}`"
                @error="$event.target.src=defaultAvatar"
              />

              <span>{{ createdBy }}</span>
            </div>
          </li>

          <!-- Modified By -->
          <li>
            <h3 class="tgbDirectoryDetailsLabel">Modified By</h3>

            <div
              :title="modifiedBy"
              class="user-details-row"
            >
              <img
                v-if="details.modifiedBy && details.modifiedBy.profileImageUrl"
                class="tgbUserIconInline"
                :src="details.modifiedBy.profileImageUrl"
                :alt="`Profile image of ${details.modifiedBy.displayName}`"
                @error="$event.target.src=defaultAvatar"
              />

              <span>{{ modifiedBy }}</span>
            </div>
          </li>

          <li>
            <h3 class="tgbDirectoryDetailsLabel">Created On</h3>
            <span>{{ createdOn ? createdOn : dataUnavailableText }}</span>
          </li>

          <li>
            <h3 class="tgbDirectoryDetailsLabel">Modified On</h3>
            <span>{{ modifiedOn ? modifiedOn : dataUnavailableText }}</span>
          </li>

          <li class="grid-col-span-2">
            <h3 class="tgbDirectoryDetailsLabel">In This Production</h3>

            <tgb-avatar-list :users="connectedUsers" />
          </li>

          <!-- Outputs Section -->
          <li class="grid-col-span-2">
            <h3 class="tgbDirectoryDetailsLabel">Outputs</h3>

            <div v-if="isLoadingScreens" class="loading loading--inline">
              <i class="icon icon-spinner"></i>
            </div>

            <div
              v-else-if="screens && screens.length"
              v-for="screen in screens"
              :key="screen._id"
              class="card production-details__screen-card"
            >
              <!-- Screen Output Status (Open / Closed) -->
              <div
                class="details__status-indicator margin-right-sm"
                :class="`details__status-indicator--${screen.status}`"
                :title="screen.connected ? 'Active' : 'Inactive'"
              />

              <div class="card-info">
                <div class="card-title">
                  {{ screen.title }}
                </div>
              </div>

              <div class="card-actions">
                <!-- Copy To Clipboard -->
                <copy-clipboard
                  class="tb-btn tb-btn-text medium-em"
                  data-entity="producer-details-copyScreenLink"
                  :data-clipboard-text="`${baseUrl}/#/${screen.token}`"
                >
                  <span class="fas fa-link"></span>
                </copy-clipboard>

                <!-- Open In New Tab -->
                <a
                  class="tb-btn tb-btn-text medium-em"
                  :href="`${baseUrl}/#/${screen.token}`"
                  target="_blank"
                  data-entity="producer-details-openScreenExternal"
                >
                  <i class="fas fa-external-link-alt"></i>
                </a>
              </div>
            </div>

            <div v-else>
              No outputs assigned to this production
            </div>
          </li>

          <!-- Tags Section -->
          <li class="grid-col-span-2">
            <h3 class="tgbDirectoryDetailsLabel">Tags</h3>
            <div class="tgb-tags">
              <span
                class="tgb-tags__tag"
                v-for="(tag, index) in details.tags"
                :key="index"
                >{{ tag }}</span
              >
            </div>

            <p
              v-if="!details.tags || !details.tags.length"
              class="margin-bottom-none"
            >
              This production has no associated tags.
            </p>
          </li>
        </ul>
      </div>

      <div class="tgbDialogActions">
        <slot name="actions"></slot>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import {format} from 'date-fns';

import CopyClipboard from '../../../js/shared/copy-clipboard/CopyClipboard.vue';
import TgbAvatarList from '../user-avatar/TgbAvatarList.vue';

export default {
  name: 'DirectoryDetails',

  components: {
    CopyClipboard,
    TgbAvatarList,
  },

  props: {
    details: {
      default: false,
      type: [Object, Boolean],
    },
  },

  data() {
    return {
      dataUnavailableText: 'No Data',
      dateFormat: 'MM/dd/yyyy p',
      tags: [],
      tagInput: '',
      defaultAvatar: './producer/assets/img/profile_placeholder.png',
      baseUrl: process.env.LIVE_URL,
    };
  },

  created() {
    this.$store.dispatch('getScreens', this.owner);
  },

  computed: {
    ...mapGetters(['hasTeams']),

    connectedUsers() {
      return this.$store.state.clients.byProduction[this.details._id];
    },

    owner() {
      return this.details.owner;
    },

    isLoadingScreens() {
      return this.$store.state.screens.isLoading[this.owner];
    },

    screens() {
      return this.$store.getters.getScreensByProduction(this.details._id);
    },

    createdOn() {
      if ('created' in this.details) {
        return format(new Date(this.details.created), this.dateFormat);
      }

      return null;
    },

    modifiedOn() {
      if ('modified' in this.details) {
        return format(new Date(this.details.modified), this.dateFormat);
      }

      return null;
    },

    ownedBy() {
      return this.details?.ownedBy?.displayName || 'N/A';
    },

    createdBy() {
      return this.details?.createdBy?.displayName || 'N/A';
    },

    modifiedBy() {
      return this.details?.modifiedBy?.displayName || 'N/A';
    },
  },
};
</script>

<style lang="scss">
.tgbDirectoryDetails {
  height: 100%;
  max-height: 745px;
}

.tgbDirectoryDetailsList {
  font-size: 14px;
  padding: 0;
  margin: 0;
  list-style: none;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-column-gap: 0.5rem;

  > li {
    &.grid-col-span-2 {
      grid-column: span 2 / span 2;
    }
    margin-bottom: 24px;
  }
}

.tgbDirectoryDetailsLabel {
  font-size: 12px;
  display: block;
  font-weight: 700;
  line-height: 1.5;
  letter-spacing: 0.1em;
  margin-bottom: 4px;
}

.tgbDialogActions {
  text-align: right;
  position: sticky;
  bottom: 0;
  padding: 1rem;
  background: #121212;
}

.TgbDirectoryDetailsContent {
  padding: 1rem 2rem;
}

.user-details-row {
  display: flex;
  align-items: center;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.production-details__screen-card {
  padding: 0.5rem 1rem;
}

.details__status-indicator {
  width: 8px;
  height: 8px;
  border-radius: 100%;
  background-color: #00D483;

  &--disconnected {
    background-color: #e65447;
  }
}
</style>
